// v1.8 P0: 把 docs/WORKFLOW.md Part 2 (订单 10 阶段) 嵌到 Trade OS
// 单源真相: WORKFLOW.md § 2.1 + order-mgmt.md § 1
// - 10 阶段: 询盘 → 核算 → PI → 接受+合同 → 定金 → 下单 → QC → 尾款 → 装柜 → 寄单+复盘
// - 当前状态计算: status + checklistProgress.completedSteps 双重信号
// - 不修改 schema — 现有 order.status 字段已够

import type { OrderStatus } from './api';

/** 10 阶段定义 (与 docs/WORKFLOW.md § 2.1 表对齐) */
export interface WorkflowStage {
  /** 0-indexed 阶段编号 (0-9) */
  index: number;
  /** 内部 id, 跟 status 对应 */
  id:
    | 'inquiry'
    | 'costing'
    | 'pi'
    | 'accept_contract'
    | 'deposit'
    | 'factory'
    | 'qc'
    | 'balance'
    | 'ship'
    | 'docs_close';
  /** 中文标签 */
  labelZh: string;
  /** 简短描述 */
  descZh: string;
  /** 下一步提示 */
  nextHintZh: string;
  /** 该阶段对应的 status 值 (决定进度条颜色) */
  statusKeys: OrderStatus[];
}

export const WORKFLOW_STAGES: WorkflowStage[] = [
  {
    index: 0,
    id: 'inquiry',
    labelZh: '询盘',
    descZh: '收到询盘, 判断真假, 入 CRM',
    nextHintZh: '24h 内回复 + 5 维评估 (国家/规模/规格/历史/邮箱)',
    statusKeys: ['draft'],
  },
  {
    index: 1,
    id: 'costing',
    labelZh: '核算',
    descZh: '工厂报价 + 海运费 + 关税 (选 Incoterm)',
    nextHintZh: '3-5 家工厂比价 + 算 6 Incoterm 价 (Trade OS 一键)',
    statusKeys: ['draft'],
  },
  {
    index: 2,
    id: 'pi',
    labelZh: 'PI',
    descZh: '发 PI 给客户 (抬头 / Incoterm / 银行信息)',
    nextHintZh: 'PI 必含: 编号 / 抬头 / SKU / Incoterm / 银行 / 有效期',
    statusKeys: ['draft'],
  },
  {
    index: 3,
    id: 'accept_contract',
    labelZh: '接受+合同',
    descZh: '客户回签 PI → 签销售合同',
    nextHintZh: '合同 10 必含条款 (主体 / 仲裁 / 单证清单)',
    statusKeys: ['draft'],
  },
  {
    index: 4,
    id: 'deposit',
    labelZh: '定金',
    descZh: '收 30% T/T 定金, 通知工厂下单',
    nextHintZh: '🔴 没到账不生产. 银行信息用公司抬头账号.',
    statusKeys: ['deposit_received'],
  },
  {
    index: 5,
    id: 'factory',
    labelZh: '下单',
    descZh: 'PO + 工艺单 + 备料通知工厂',
    nextHintZh: '工厂回签 24h 内 + PP sample 客户确认',
    statusKeys: ['producing'],
  },
  {
    index: 6,
    id: 'qc',
    labelZh: 'QC',
    descZh: 'DPI 中期 + FRI 尾期验货 (必亲临)',
    nextHintZh: 'FRI 4 类照片: 外观/包装/内包装/集装箱封条',
    statusKeys: ['qc'],
  },
  {
    index: 7,
    id: 'balance',
    labelZh: '尾款',
    descZh: '收 70% T/T 尾款, 准备装柜',
    nextHintZh: '🔴 没到账不装柜. CIF 货值 > USD 20k 建议买中信保.',
    statusKeys: ['balance_received'],
  },
  {
    index: 8,
    id: 'ship',
    labelZh: '装柜',
    descZh: '订舱 + 拖车 + 报关 + 装柜 + 提单',
    nextHintZh: 'HS Code 必正确 + 封条号入 Trade OS + CI/PL 100% 跟 PI',
    statusKeys: ['shipped'],
  },
  {
    index: 9,
    id: 'docs_close',
    labelZh: '寄单+复盘',
    descZh: '寄全套单证 (CI/PL/BL/CO) + AI 复盘 5 段',
    nextHintZh: '单证 PDF 存 R2 (5 年) + 客户收货 30 天后无异议自动接受',
    statusKeys: ['closed'],
  },
];

/** status → 阶段 index 映射 (保守映射: 取最匹配的阶段) */
const STATUS_TO_STAGE: Record<OrderStatus, number> = {
  draft: 0, // 草稿默认 0 阶段 (询盘), 由 checklist progress 进一步判断
  deposit_received: 4,
  producing: 5,
  qc: 6,
  balance_received: 7,
  shipped: 8,
  closed: 9,
  cancelled: 0, // 取消回到 0
};

/**
 * 计算订单当前阶段 (0-9)
 * 双重信号:
 *   1. status 字段: 决定中后段 (deposit/balance/shipped/closed)
 *   2. checklistProgress.completedSteps: 决定前段 (0-3)
 *   取两者中较大者 (更靠后 = 更"完成")
 */
export function deriveCurrentStage(
  status: OrderStatus,
  completedSteps: number,
  totalSteps: number,
): number {
  const fromStatus = STATUS_TO_STAGE[status] ?? 0;
  if (status === 'draft' || status === 'cancelled') {
    // draft 时, 用 checklist 完成度推算 0-3 阶段
    // FOB=12 步: 阶段 0-3 各占 3 步 (询盘/PI/接受/合同)
    // CIF=14: 前 4 阶段各占 3-4 步
    // 简单映射: 0 步→0, 1 步→1 (PI), 2 步→2 (接受), 3 步→3 (合同), 4+ 步→3
    if (completedSteps <= 0) return 0;
    if (completedSteps <= 1) return 1;
    if (completedSteps <= 2) return 2;
    return 3;
  }
  // 非 draft: status 决定中后段
  return fromStatus;
}

/** 给定当前阶段, 返回"下一步该做什么"提示 */
export function getNextHint(stageIndex: number): string {
  const stage = WORKFLOW_STAGES[stageIndex];
  if (!stage) return '';
  return stage.nextHintZh;
}

/** 是否完成 (Stage 9) */
export function isClosed(status: OrderStatus): boolean {
  return status === 'closed' || status === 'cancelled';
}

/** 阶段进度百分比 (0-100) */
export function stageProgress(stageIndex: number): number {
  return Math.round(((stageIndex + 1) / WORKFLOW_STAGES.length) * 100);
}

// =================================================================
// 5 红线 (与 docs/WORKFLOW.md § 2.14 红线 5 必背 对齐)
// =================================================================
export interface Redline {
  id: string;
  shortZh: string;
  titleZh: string;
  bodyZh: string;
  /** 严重性: critical = 资金风险, important = 合规风险, advisory = 流程 */
  severity: 'critical' | 'important' | 'advisory';
}

export const REDLINES: Redline[] = [
  {
    id: 'payment_first',
    shortZh: '没到账不动',
    titleZh: '资金红线: 没到账不动',
    bodyZh:
      '30% 定金 + 70% 尾款 没到账绝对不生产/不装柜. 客户口头/邮件承诺不算. 银行到账回单 + Trade OS 状态变更 才算生效.',
    severity: 'critical',
  },
  {
    id: 'triangle_clean',
    shortZh: '三角架构不混',
    titleZh: '合规红线: 三角架构不混',
    bodyZh:
      '🇯🇵 日本客户 → 🇯🇵 公司抬头 PI / 签约; 🇺🇸 美国客户 → 🇨🇳 主体签约; 转口业务 → 🇭🇰 抬头. 主体错了 = 关税 + 客户信任 双失.',
    severity: 'critical',
  },
  {
    id: 'hs_code',
    shortZh: 'HS 必正确',
    titleZh: '合规红线: HS Code 必正确',
    bodyZh:
      '玩具 9503 / 9504 / 塑料 3926 / 圣诞装饰 9505. HS 错 = 关税差异 + 商检被卡 + 退税受阻. PI / 报关单 / 装箱单 三处一致.',
    severity: 'important',
  },
  {
    id: 'docs_match_pi',
    shortZh: '单证 100% 跟 PI 一致',
    titleZh: '合规红线: CI/PL/BL/CO 100% 跟 PI 一致',
    bodyZh:
      '商业发票 (CI) / 装箱单 (PL) / 提单 (B/L) / 原产地证 (CO) 上的产品名/数量/单价/金额 必须 100% 跟 PI 一致. 客户清关核心文件, 错 = 滞港 + 罚款.',
    severity: 'important',
  },
  {
    id: 'postmortem_required',
    shortZh: '复盘必做',
    titleZh: '复盘红线: 每单必做',
    bodyZh:
      '每单关闭后 24h 内做 5 段 AI 复盘: 时间线 / 利润分解 / 资金流 / 合规 / 改进建议. 利润 < 5% 必找 1 个改进点, < 0 必找 3 个. 不复盘 = 同样的错重复犯.',
    severity: 'advisory',
  },
];
